import {
  Button,
  Combobox,
  type ComboboxOption,
  Modal,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  fetchAllCompanies,
  type SettingStepStatus,
  useYard,
  useYardSettingSteps,
  YardScreenHeader,
} from "./components";

type SettingStep = {
  key: string;
  title: string;
  description: string;
  /** Relative path of the destination page, or undefined if not built yet. */
  to?: string;
};

const STEPS: SettingStep[] = [
  {
    key: "decoupage",
    title: "Découpage géographique",
    description: "Enregistrement des bâtiments et zones",
    to: "decoupage",
  },
  {
    key: "nature-travaux",
    title: "Nature des travaux",
    description: "Enregistrement des lots et prestations",
    to: "nature-travaux",
  },
  {
    key: "donneurs-ordre",
    title: "Contrats et équipes donneurs d'ordre",
    description: "Enregistrement des contrats AMO, CT, SPS et MOE",
    to: "donneurs-ordre",
  },
  {
    key: "contrats-equipes-travaux",
    title: "Contrats et équipes travaux",
    description:
      "Enregistrement des contrats, équipes et périmètre d'intervention",
    to: "travaux",
  },
];

const STATUS_OPTIONS: SelectOption<SettingStepStatus>[] = [
  { label: "Non démarré", value: "not_started" },
  { label: "Démarré", value: "started" },
  { label: "OK pour l'heure", value: "ok" },
];

/**
 * Screen 903 — "Menu paramétrage chantier".
 * Navigation hub listing the configuration steps of a yard. Built steps link to
 * their page; steps not yet available are shown disabled with an "À venir" tag.
 * Each step carries a manually-set progress status.
 */
export function YardSettingsMenu() {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useNavigate();
  const numericYardId = Number(yardId);
  const { yard, loading, refetch } = useYard(numericYardId);
  const { statuses, setStatus } = useYardSettingSteps(numericYardId);
  const [moaModalOpen, setMoaModalOpen] = useState(false);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Chargement...
        </div>
      </div>
    );
  }

  if (!yard) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Chantier introuvable
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <YardScreenHeader
        yardName={yard.name}
        yardState={yard.state}
        screenTitle="Menu paramétrage chantier"
        onBack={() => navigate("/staff")}
        backLabel="Retour à l'accueil staff"
      />

      <button
        type="button"
        onClick={() => setMoaModalOpen(true)}
        className="mb-6 text-sm text-zinc-600 transition-colors hover:text-blue-700 dark:text-zinc-400 dark:hover:text-blue-400"
      >
        MOA : {yard.moaCompanyName ?? "non renseignée"}
      </button>

      <ul className="max-w-3xl divide-y divide-zinc-200 overflow-hidden rounded-lg border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
        {STEPS.map((step) => {
          const available = isNotNil(step.to);
          const status = statuses[step.key] ?? "not_started";

          return (
            <li
              key={step.key}
              className="flex items-center justify-between gap-4 px-4 py-3"
            >
              <div className="min-w-0 flex-1 text-sm">
                {available ? (
                  <Link
                    to={step.to ?? ""}
                    className="font-medium text-zinc-900 transition-colors hover:text-blue-700 dark:text-zinc-100 dark:hover:text-blue-400"
                  >
                    {step.title}
                  </Link>
                ) : (
                  <span className="font-medium text-zinc-500">
                    {step.title}
                  </span>
                )}
                <span className="text-zinc-500"> : {step.description}</span>
                {!available && (
                  <span className="ml-2 rounded bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                    À venir
                  </span>
                )}
              </div>
              <div className="w-52 shrink-0">
                <Select
                  options={STATUS_OPTIONS}
                  value={[status]}
                  onValueChange={({ value }) => {
                    const next = value[0];
                    if (isNotNil(next)) setStatus(step.key, next);
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <MoaCompanyModal
        open={moaModalOpen}
        onOpenChange={setMoaModalOpen}
        yardId={numericYardId}
        currentMoaCompanyId={yard.moaCompanyId}
        onSuccess={refetch}
      />
    </div>
  );
}

type MoaCompanyModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yardId: number;
  currentMoaCompanyId: number | null;
  onSuccess: () => void;
};

/** Lets the user designate the MOA (maître d'ouvrage) company for the yard. */
function MoaCompanyModal({
  open,
  onOpenChange,
  yardId,
  currentMoaCompanyId,
  onSuccess,
}: MoaCompanyModalProps) {
  const [companyOptions, setCompanyOptions] = useState<ComboboxOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    currentMoaCompanyId,
  );
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedCompanyId(currentMoaCompanyId);

    async function fetchCompanies() {
      setIsLoadingCompanies(true);
      const { companies } = await fetchAllCompanies();
      if (isNil(companies)) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de charger les entreprises",
        });
        setIsLoadingCompanies(false);
        return;
      }

      setCompanyOptions(
        companies.map((company) => ({
          label: company.name,
          value: String(company.id),
        })),
      );
      setIsLoadingCompanies(false);
    }
    fetchCompanies();
  }, [open, currentMoaCompanyId]);

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await apiClient.PATCH("/api/admin/yards/{id}", {
      params: { path: { id: yardId } },
      body: { moaCompanyId: selectedCompanyId },
    });
    setIsSaving(false);

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la modification du MOA",
      });
      return;
    }

    toaster.success({
      title: "Succès",
      description: "MOA modifié avec succès",
    });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="sm">
            <Modal.Header>
              <div>
                <Modal.Title>Maître d'ouvrage</Modal.Title>
                <Modal.Description>
                  Désignez l'entreprise MOA de ce chantier
                </Modal.Description>
              </div>
            </Modal.Header>
            <Modal.Body>
              <Combobox
                options={companyOptions}
                value={selectedCompanyId ? [String(selectedCompanyId)] : []}
                onValueChange={(details) => {
                  const value = details.value[0];
                  setSelectedCompanyId(value ? Number(value) : null);
                }}
                placeholder="Rechercher une entreprise"
                disabled={isLoadingCompanies}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="button"
                outline
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Annuler
              </Button>
              <Button type="button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "En cours..." : "Enregistrer"}
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
